import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowUpRight } from 'lucide-react';
import { pedirCatalogo, type FuenteDelCatalogo } from '../../../common/services/catalogo.js';
import { PortadaEstudio } from '../../../common/components/PortadaEstudio.js';
import type { Idioma } from '../../../i18n/index.js';

/** Solo fechas de alta conocidas: el año del informe no dice cuándo llegó a Athena. */
export function ultimosEstudios(fuentes: readonly FuenteDelCatalogo[]): readonly FuenteDelCatalogo[] {
  return fuentes
    .filter(f => f.anadidoEn != null && Number.isFinite(Date.parse(f.anadidoEn)))
    .sort((a, b) => Date.parse(b.anadidoEn ?? '') - Date.parse(a.anadidoEn ?? '') || a.slug.localeCompare(b.slug))
    .slice(0, 12);
}

const TEXTOS = {
  es: { titulo: 'Últimos estudios añadidos', todos: 'Ver todos los estudios' },
  en: { titulo: 'Latest studies added', todos: 'View all studies' },
} as const;

export function UltimosEstudios({ idioma }: { readonly idioma: Idioma }) {
  const [fuentes, setFuentes] = useState<readonly FuenteDelCatalogo[]>([]);
  useEffect(() => {
    let vigente = true;
    void pedirCatalogo().then(c => {
      if (vigente) setFuentes(ultimosEstudios(c?.fuentes ?? []));
    });
    return () => {
      vigente = false;
    };
  }, []);
  if (fuentes.length === 0) return null;
  const t = TEXTOS[idioma];
  return (
    <section aria-labelledby='ultimos-estudios' className='mt-16 w-full max-w-[1160px] border-t border-(--line) pt-10'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <h2 id='ultimos-estudios' className='m-0 font-(family-name:--serif) text-[28px] leading-tight font-semibold'>
          {t.titulo}
        </h2>
        <Link
          to='/fuentes'
          className='inline-flex items-center gap-2 rounded-full border border-(--line-strong) px-4 py-2 text-[13px] font-medium text-(--teal-deep) transition-colors hover:border-(--teal-deep) hover:bg-(--chat-bg) focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-(--teal-deep)'
        >
          {t.todos}
          <ArrowUpRight aria-hidden='true' size={17} />
        </Link>
      </div>
      <ul className='mt-8 mb-0 list-none columns-1 gap-5 p-0 sm:columns-2 lg:columns-3 xl:columns-4'>
        {fuentes.map(f => (
          <li key={f.slug} className='mx-auto mb-5 max-w-[324px] min-w-0 break-inside-avoid sm:max-w-none'>
            <Link
              to={`/fuentes/${f.slug}`}
              className='group flex flex-col items-start rounded-xl border border-(--line) bg-(--chat-bg) p-3 transition-[border-color,box-shadow] hover:border-(--teal-deep) hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-(--teal-deep)'
            >
              <PortadaEstudio url={f.portadaUrl} marco={false} />
              <h3 className='m-0 mt-4 text-[16px] leading-[1.4] font-semibold break-words text-(--ink) group-hover:text-(--teal-deep)'>
                {f.tituloOriginal}
              </h3>
              <div className='mt-3 flex w-full items-start justify-between gap-3 border-t border-(--line) pt-3'>
                <p className='m-0 min-w-0 text-[12px] leading-[1.5] break-words text-(--ink-soft)'>{f.editor}</p>
                {f.anio != null && (
                  <span className='shrink-0 rounded-md bg-(--paper) px-2 py-1 text-[11px] leading-none font-medium tabular-nums text-(--ink-soft)'>
                    {f.anio}
                  </span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
